import { useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStore } from "@/stores/store-context";
import { requestErrorSummary } from "@/lib/errors/request-error-summary";

interface DeactivateAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DeactivateAccountModal = observer(function DeactivateAccountModal({
  open,
  onOpenChange,
}: DeactivateAccountModalProps) {
  const { t } = useTranslation();
  const { user } = useStore();

  const [confirmValue, setConfirmValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const username = user.currentUser?.username ?? "";
  const canConfirm = Boolean(username) && confirmValue.trim() === username;

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setConfirmValue("");
      setIsSubmitting(false);
    }, 200);
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsSubmitting(true);
    try {
      await user.deactivate();
      // No navigate: the sign-out form's page load leaves (NG-2513, AuthService.signOut).
      await user.signOut("deactivated");
    } catch (error) {
      console.error("[profile] account deactivation failed", requestErrorSummary(error));
      toast.error(t("profile_settings.deactivate.error"));
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("profile_settings.deactivate.title")}</DialogTitle>
          <DialogDescription>{t("profile_settings.deactivate.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="deactivate-confirm">
            {t("profile_settings.deactivate.confirm_label")}
          </Label>
          <Input
            id="deactivate-confirm"
            type="text"
            value={confirmValue}
            onChange={(event) => setConfirmValue(event.target.value)}
            placeholder={t("profile_settings.deactivate.confirm_placeholder", { username })}
            autoFocus
            disabled={isSubmitting}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={!canConfirm || isSubmitting}
          >
            {isSubmitting
              ? t("profile_settings.deactivate.confirming")
              : t("profile_settings.deactivate.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
